import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiTrendingUp, FiTrendingDown, FiAlertCircle, FiCheckCircle } = FiIcons;

const ProductVarianceTable = () => {
  // Mock Data for Product Wise Variance
  const productData = [
    {
      id: 1,
      groupName: 'Abiraterone Acetate',
      sku: "250mg 120's count",
      ndc: '72005003092',
      budgetedVol: 17002,
      forecastedVol: 13000,
      actualVol1to10: 3126,
      actualVol11to20: 5136,
      actualVol21to30: 0,
    },
    {
      id: 2,
      groupName: 'Abiraterone Acetate',
      sku: "500mg 60's count",
      ndc: '72005028560',
      budgetedVol: 1004,
      forecastedVol: 900,
      actualVol1to10: 432,
      actualVol11to20: 240,
      actualVol21to30: 0,
    },
    {
      id: 3,
      groupName: 'Eltrombopag',
      sku: "25mg 30's count",
      ndc: '72005012345',
      budgetedVol: 5000,
      forecastedVol: 4800,
      actualVol1to10: 1500,
      actualVol11to20: 1600,
      actualVol21to30: 1800,
    },
    {
      id: 4,
      groupName: 'Eltrombopag',
      sku: "50mg 30's count",
      ndc: '72005067890',
      budgetedVol: 3500,
      forecastedVol: 3200,
      actualVol1to10: 1100,
      actualVol11to20: 1050,
      actualVol21to30: 1150,
    },
    {
      id: 5,
      groupName: 'Brivaracetam',
      sku: "10mg 60's count",
      ndc: '72005011223',
      budgetedVol: 2000,
      forecastedVol: 1900,
      actualVol1to10: 600,
      actualVol11to20: 650,
      actualVol21to30: 700,
    },
    {
      id: 6,
      groupName: 'Mirabegron',
      sku: "50mg 30's count",
      ndc: '72005044556',
      budgetedVol: 8000,
      forecastedVol: 7500,
      actualVol1to10: 2500,
      actualVol11to20: 2600,
      actualVol21to30: 2800,
    },
    {
      id: 7,
      groupName: 'Venetoclax',
      sku: "100mg 120's count",
      ndc: '72005099887',
      budgetedVol: 1200,
      forecastedVol: 1100,
      actualVol1to10: 350,
      actualVol11to20: 400,
      actualVol21to30: 380,
    }
  ];

  // Helper to calculate totals and variances
  const processedData = productData.map(item => {
    const totalActual = item.actualVol1to10 + item.actualVol11to20 + item.actualVol21to30;
    const budgetVariance = item.budgetedVol > 0 ? ((totalActual - item.budgetedVol) / item.budgetedVol) * 100 : 0;
    const forecastVariance = item.forecastedVol > 0 ? ((totalActual - item.forecastedVol) / item.forecastedVol) * 100 : 0;

    return {
      ...item,
      totalActual,
      budgetVariance,
      forecastVariance
    };
  });

  // KPIs
  const totalBudget = processedData.reduce((acc, curr) => acc + curr.budgetedVol, 0);
  const totalActual = processedData.reduce((acc, curr) => acc + curr.totalActual, 0);
  const totalVariance = totalBudget > 0 ? ((totalActual - totalBudget) / totalBudget) * 100 : 0;
  
  const positiveVarianceCount = processedData.filter(i => i.budgetVariance > 0).length;
  const negativeVarianceCount = processedData.filter(i => i.budgetVariance < 0).length;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { 
            label: 'Total Budgeted Volume', 
            value: totalBudget.toLocaleString(), 
            icon: FiTrendingUp, 
            color: 'text-blue-600', 
            bg: 'bg-blue-50' 
          },
          { 
            label: 'Total Actual Sold', 
            value: totalActual.toLocaleString(), 
            icon: FiCheckCircle, 
            color: 'text-green-600', 
            bg: 'bg-green-50' 
          },
          { 
            label: 'Overall Variance', 
            value: `${totalVariance.toFixed(2)}%`, 
            icon: totalVariance >= 0 ? FiTrendingUp : FiTrendingDown, 
            color: totalVariance >= 0 ? 'text-green-600' : 'text-red-600', 
            bg: totalVariance >= 0 ? 'bg-green-50' : 'bg-red-50' 
          },
          { 
            label: 'Products Below Budget', 
            value: negativeVarianceCount, 
            icon: FiAlertCircle, 
            color: 'text-orange-600', 
            bg: 'bg-orange-50' 
          }
        ].map((kpi, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${kpi.bg}`}>
                <SafeIcon icon={kpi.icon} className={kpi.color} />
              </div>
              <div>
                <p className="text-sm text-gray-500">{kpi.label}</p>
                <p className="text-xl font-bold text-gray-900">{kpi.value}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Product Wise Variance Report</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-semibold">Product Group Name</th>
                <th className="px-4 py-3 font-semibold">SKU</th>
                <th className="px-4 py-3 font-semibold">NDC</th>
                <th className="px-4 py-3 font-semibold text-right">Budgeted Volume (Pack)<br/>Current Month</th>
                <th className="px-4 py-3 font-semibold text-right">Forecasted Volume (Pack)<br/>Current Month</th>
                <th className="px-4 py-3 font-semibold text-right">Actual Sold Vol<br/>Day 1-10</th>
                <th className="px-4 py-3 font-semibold text-right">Actual Sold Vol<br/>Day 11-20</th>
                <th className="px-4 py-3 font-semibold text-right">Actual Sold Vol<br/>Day 21-30</th>
                <th className="px-4 py-3 font-semibold text-right">Total Actual<br/>Sold Vol</th>
                <th className="px-4 py-3 font-semibold text-right">% Variance<br/>Budget Vs Actuals</th>
                <th className="px-4 py-3 font-semibold text-right">% Variance<br/>Forecast Vs Actuals</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {processedData.map((item, index) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900">{item.groupName}</td>
                  <td className="px-4 py-3 text-gray-700">{item.sku}</td>
                  <td className="px-4 py-3 text-gray-500 font-mono">{item.ndc}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{item.budgetedVol.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{item.forecastedVol.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{item.actualVol1to10.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{item.actualVol11to20.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{item.actualVol21to30.toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-medium text-blue-600">{item.totalActual.toLocaleString()}</td>
                  <td className={`px-4 py-3 text-right font-medium ${item.budgetVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {item.budgetVariance.toFixed(2)}%
                  </td>
                  <td className={`px-4 py-3 text-right font-medium ${item.forecastVariance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {item.forecastVariance.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductVarianceTable;